/**
 * @experimental
 * This component is not yet officially supported by MADS.
 * It is subject to change and may be modified in future versions.
 */

import type { StrictSpacingStyle } from "../util/types";

import { Box } from "../Box";
import { ChevronDown } from "../icons";
import { useTheme } from "../ThemeProvider";
import { useFocusRing } from "../util/useFocusRing";
import { useIsReducedMotionEnabled } from "../util/useIsReducedMotionEnabled";
import { WrapWithTextIfNeeded } from "../util/WrapWithTextIfNeeded";

import { type FC, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Pressable, StyleSheet, View, Animated } from "react-native";

export type DetailsSize = "md" | "lg";

export interface DetailsProps {
  /**
   * Section heading content
   */
  heading: ReactNode;

  /**
   * Control option for display Details opened or closed. Defaults to false
   */
  open?: boolean;

  /**
   * Default state of the Details when rendered.
   */
  defaultOpen?: boolean;

  /**
   * Callback function when a Details changes from open to closed and vice versa
   * this function will always call the opposite of what the current open state is (true/false)
   * @param openState
   */
  onOpenChange?: (openState: boolean) => void;

  /**
   * Size prop for the Details component
   * defaults to 'md'
   */
  size?: DetailsSize;

  /**
   * Additional styles for the container
   */
  sx?: StrictSpacingStyle;

  /**
   * Children content
   */
  children: ReactNode;
}

export const Details: FC<DetailsProps> = ({
  children,
  heading,
  onOpenChange,
  open,
  defaultOpen,
  size = "md",
  sx,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen ?? open ?? false);
  const toggleAnimation = useRef(new Animated.Value((defaultOpen ?? open) ? 1 : 0));
  const [bodyHeight, setBodyHeight] = useState(0);
  const isReducedMotionEnabled = useIsReducedMotionEnabled();
  const detailsBodyID = useId();
  const theme = useTheme();
  const { isFocusVisible, focusProps } = useFocusRing();
  const contentRef = useRef<View>(null);

  useEffect(() => {
    if (open !== undefined && open !== isOpen) {
      setIsOpen(open);
      onOpenChange?.(open);
    }
  }, [open, isOpen, onOpenChange]);

  useEffect(() => {
    const delay = isReducedMotionEnabled ? 0 : 10;
    const animationDuration = isReducedMotionEnabled ? 0 : theme.durations.quick;

    toggleAnimation.current.setValue(isOpen ? 0 : 1);
    const animation = Animated.timing(toggleAnimation.current, {
      toValue: isOpen ? 1 : 0,
      duration: animationDuration,
      useNativeDriver: false,
    });
    const timeout = setTimeout(() => {
      animation.start();
    }, delay);

    return () => {
      clearTimeout(timeout);
      animation.stop();
    };
    // isReducedMotionEnabled is not included in the dependency array because it is a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const el = contentRef.current as unknown as HTMLElement | null;
    if (!el?.setAttribute) return;
    if (!isOpen) {
      el.setAttribute("inert", "");
    } else {
      el.removeAttribute("inert");
    }
  }, [isOpen]);

  return (
    <Box
      sx={{
        paddingVertical: size === "md" ? theme.spacing.s3 : theme.spacing.s4,
        ...styles.container,
        ...sx,
      }}
    >
      <Pressable
        style={{
          ...styles.summary,
          ...(isFocusVisible
            ? {
                outlineStyle: "solid",
                outlineWidth: 2,
                outlineColor: theme.colors.utilityFocus,
                outlineOffset: 2,
              }
            : undefined),
        }}
        onPress={() => {
          setIsOpen((isOpen) => {
            onOpenChange?.(!isOpen);
            return !isOpen;
          });
        }}
        accessible={true}
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
        aria-expanded={isOpen}
        aria-controls={detailsBodyID}
        onFocus={focusProps.onFocus}
        onBlur={focusProps.onBlur}
      >
        <WrapWithTextIfNeeded
          level={size === "lg" ? 4 : 3}
          sx={{
            color: theme.colors.foregroundStrong,
            paddingInlineEnd: theme.spacing.s1,
          }}
        >
          {heading}
        </WrapWithTextIfNeeded>
        <Animated.View
          style={{
            transform: [
              {
                rotate: toggleAnimation.current.interpolate({
                  inputRange: [0, 1],
                  outputRange: ["0deg", "180deg"],
                }),
              },
            ],
          }}
          aria-hidden={true}
        >
          <ChevronDown size="md" fill={theme.colors.foregroundStrong} />
        </Animated.View>
      </Pressable>

      <Animated.View
        style={[
          styles.body,

          {
            height: toggleAnimation.current.interpolate({
              inputRange: [0, 1],
              outputRange: [0, bodyHeight],
            }),
            opacity: isOpen ? 1 : 0,
          },
        ]}
        aria-hidden={!isOpen}
      >
        <View
          ref={contentRef}
          id={detailsBodyID}
          style={styles.content}
          onLayout={({ nativeEvent }) => {
            setBodyHeight(nativeEvent.layout.height);
          }}
        >
          <WrapWithTextIfNeeded>{children}</WrapWithTextIfNeeded>
        </View>
      </Animated.View>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  summary: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "transparent",
  },
  body: {
    width: "100%",
    overflow: "hidden",
  },
  content: {
    width: "100%",
    position: "absolute",
    display: "flex",
  },
});
